import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSupportSession, subscribeSupportSession } from "@/lib/supportSession";
import { enforceCacheOwner } from "@/lib/tenantCacheGuard";

export const useCompany = () => {
  const [companyId, setCompanyId] = useState<string | null>(() => getSupportSession()?.companyId ?? null);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<Array<{ company_id: string; is_owner: boolean }>>([]);
  const currentUserId = useRef<string | null>(null);

  const fetchCompanyInfo = useCallback(async () => {
      try {
        const support = getSupportSession();
        if (support) {
          // Impersonating a customer company — behave as that company's owner.
          setCompanyId(support.companyId);
          setIsOwner(true);
          setLoading(false);
          return;
        }

        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        currentUserId.current = user?.id ?? null;
        
        if (!user) {
          // Signed out: the previous tenant must not survive in memory, or
          // tenant-scoped UI (e.g. the DEMO ENVIRONMENT badge) keeps rendering
          // on the public sign-in screen after an ordinary logout.
          setCompanyId(null);
          setIsOwner(false);
          setCompanies([]);
          setLoading(false);
          return;
        }


        // Get ALL companies the user is a member of
        const { data: memberships, error } = await supabase
          .from("company_members")
          .select("company_id, is_owner")
          .eq("user_id", user.id);

        if (error) {
          console.error("Error fetching company info:", error);
          setLoading(false);
          return;
        }

        if (memberships && memberships.length > 0) {
          // A support session may have started while this request was in
          // flight. Its target is authoritative and must never be overwritten.
          if (getSupportSession()) return;
          setCompanies(memberships);
          
          // Check localStorage for selected company
          const savedCompanyId = localStorage.getItem('selected_company_id');
          
          // If saved company exists in user's companies, use it
          const savedCompanyExists = memberships.find(m => m.company_id === savedCompanyId);
          
          if (savedCompanyExists) {
            setCompanyId(savedCompanyId);
            setIsOwner(savedCompanyExists.is_owner);
          } else {
            // Use first company and save to localStorage
            setCompanyId(memberships[0].company_id);
            setIsOwner(memberships[0].is_owner);
            localStorage.setItem('selected_company_id', memberships[0].company_id);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error("Error in useCompany:", error);
        setLoading(false);
      }
  }, []);

  // Super Admin Support Mode: the active support session always wins over the
  // admin's own company selection (and never touches localStorage).
  useEffect(() => subscribeSupportSession((s) => {
    if (s) {
      setCompanyId(s.companyId);
      setIsOwner(true);
      setLoading(false);
    } else {
      setCompanyId(null);
      setIsOwner(false);
      setLoading(true);
      void fetchCompanyInfo();
    }
  }), [fetchCompanyInfo]);

  useEffect(() => {

    fetchCompanyInfo();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      // CRITICAL: Do NOT await inside this callback — it deadlocks the auth state machine.
      // TOKEN_REFRESHED removed — company doesn't change on token refresh and it
      // floods the API on iOS cold starts causing splash screen freezes.
      if (event === "SIGNED_IN" || event === "SIGNED_OUT") {
        fetchCompanyInfo();
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchCompanyInfo]);

  const switchCompany = (newCompanyId: string) => {
    const company = companies.find(c => c.company_id === newCompanyId);
    if (company) {
      setCompanyId(newCompanyId);
      setIsOwner(company.is_owner);
      localStorage.setItem('selected_company_id', newCompanyId);
      // Tenant boundary: switching company is not an auth transition, so the
      // cache guard's auth listener never fires. Purge every tenant-scoped
      // cache here so the previous company's jobs can never render.
      void enforceCacheOwner(currentUserId.current ?? "", newCompanyId);
    }
  };


  return { companyId, isOwner, loading, companies, switchCompany };
};
